/**
 * Offline Queue Utility
 * Intercepts failed POST/PUT/DELETE requests, stores them in IndexedDB,
 * and replays them when connectivity returns.
 */

const DB_NAME = 'eim-offline-sync';
const STORE_NAME = 'queue';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Queue a failed request for later replay */
export async function queueRequest(url, method, headers, body) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).add({
      url,
      method,
      headers: Object.fromEntries(
        Object.entries(headers || {}).filter(([k]) => k.toLowerCase() !== 'content-length')
      ),
      body: body || null,
      timestamp: Date.now()
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Get count of queued requests */
export async function getQueueCount() {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).count();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(0);
    });
  } catch {
    return 0;
  }
}

/** Replay all queued requests (called when back online) */
export async function replayQueue() {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readonly');
  const entries = await new Promise((resolve, reject) => {
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

  let replayed = 0;
  for (const entry of entries) {
    try {
      const response = await fetch(entry.url, {
        method: entry.method,
        headers: entry.headers,
        body: entry.body
      });
      if (response.ok) {
        const delTx = db.transaction(STORE_NAME, 'readwrite');
        delTx.objectStore(STORE_NAME).delete(entry.id);
        replayed++;
      }
    } catch {
      // Still offline, stop trying
      break;
    }
  }
  return replayed;
}

/**
 * Wrap an axios instance to auto-queue failed mutations when offline.
 * Call this once with your axios instance.
 */
export function setupOfflineInterceptor(axiosInstance) {
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      // Only queue mutation requests (not GETs) that failed due to network error
      if (
        error.message === 'Network Error' &&
        error.config &&
        error.config.method !== 'get'
      ) {
        const { url, method, headers, data } = error.config;
        await queueRequest(url, method.toUpperCase(), headers, data);

        // Return a fake success so the UI doesn't break
        return {
          data: { queued: true, message: 'Saved offline. Will sync when back online.' },
          status: 202,
          statusText: 'Queued Offline',
          headers: {},
          config: error.config
        };
      }
      return Promise.reject(error);
    }
  );

  // When back online, replay the queue
  window.addEventListener('online', async () => {
    const count = await replayQueue();
    if (count > 0) {
      // Notify the app that data was synced
      window.dispatchEvent(new CustomEvent('eim-sync-complete'));
    }
  });
}
